import { ApplicationRef, Injectable } from "@angular/core";
import { configureChains, connect, createClient, fetchBalance, prepareWriteContract, readContracts, switchNetwork, watchAccount, watchNetwork, writeContract, disconnect, waitForTransaction } from '@wagmi/core'
import { jsonRpcProvider  } from '@wagmi/core/providers/jsonRpc';
import { publicProvider } from '@wagmi/core/providers/public';
import { bsc, bscTestnet, goerli, mainnet} from '@wagmi/core/chains';
import { MetaMaskConnector } from '@wagmi/core/connectors/metaMask';
import { WalletConnectConnector } from '@wagmi/core/connectors/walletConnect';
import { ethers } from 'ethers';

import { PopupService } from "./popup.service";
import { EventService } from "./event.service";
import { getClickId, SwapStatus } from "../shared/constants/app-enums";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { Web3Auth } from "@web3auth/modal";
import { TorusWalletConnectorPlugin } from "@web3auth/torus-wallet-connector-plugin";
import { CHAIN_NAMESPACES, WALLET_ADAPTERS } from "@web3auth/base";
import { Web3AuthConnector } from "@web3auth/web3auth-wagmi-connector";
import { OpenloginAdapter } from "@web3auth/openlogin-adapter";
import { TranslateService } from "@ngx-translate/core";

const presaleBsc = require('src/assets/contracts/presaleBSC.json');
const presaleEth = require('src/assets/contracts/presaleETH.json');
const token = require('src/assets/contracts/token.json');

const presaleContractBsc = {
  address: environment.bsc.presaleAddress as any,
  abi: presaleBsc.abi,
}
const presaleContractEth = {
  address: environment.eth.presaleAddress as any,
  abi: presaleEth.abi,
}

const web3clientId = environment.web3clientId
const googleClientId = environment.googleClientId
const torusWalletId = environment.torusWalletId

@Injectable({
  providedIn: 'root'
})

export class WalletConnectService {

  swapStatus = SwapStatus.not_started;
  client: any;
  wagmiData: any;
  walletAddress: string = '';
  chainId: any;

  balanceData: {
    nativeBal: number,
    usdtBal: number,
    ethTokenSold: number,
    bscTokenSold: number,
    totalTokensSold: number,
    ethClaimable?: number,
    bscClaimable?: number,
    userClaimable: number,
    currentStep: number,
    startTime?: any,
    endTime?: any,
    paused?: boolean,
    usdRaised?: number
  } = {
    nativeBal: 0,
    usdtBal: 0,
    ethTokenSold: 0,
    bscTokenSold: 0,
    totalTokensSold: 0,
    ethClaimable:0,
    bscClaimable:0,
    userClaimable: 0,
    startTime: '',
    endTime: '',
    paused: false,
    currentStep: 0,
    usdRaised: 0
  }

  metaConnector: any;
  walletConnector: any;
  metaWalletConnector: any;
  web3AuthConnector: any;
  web3AuthInstance: Web3Auth | null = null;
  torusPlugin: TorusWalletConnectorPlugin | null = null;
  presaleData: any;
  swapHash: any = '';
  refreshId: any;
  refreshRate: number = 5000;

  tokenomics = [{
    minToken: 0,
    maxToken: 5500000000,
    maxAmount: 16500000,
    tokenUSDT: 0.003,
    title: "stage_1",
    endDate: 1679101200
  },
  {
    minToken: 5500000000,
    maxToken: 6000000000,
    maxAmount: 18750000,
    tokenUSDT: 0.0045,
    title: "last_stage",
    endDate: 1679749200
  }];

  constructor(
    private ref: ApplicationRef,
    private popupService: PopupService,
    private eventService: EventService,
    private http: HttpClient,
    private transletService: TranslateService
  ) {}

  activateSite(): void {
    // Wagmi Core Client
    const currentChain: any = environment.production ? [mainnet, bsc] : [goerli, bscTestnet];
    const { chains, provider, webSocketProvider } = configureChains(
       currentChain,
      [ publicProvider({ priority: 0 }),
        jsonRpcProvider({
        priority: 1,
        rpc: (chain) => ({
          http: chain.id === environment.eth.chainIdInt ? environment.eth.rpcURL: environment.bsc.rpcURL,
        }),
      }),
      jsonRpcProvider({
        priority: 2,
        rpc: (chain) => ({
          http: chain.id === environment.eth.chainIdInt ? environment.eth.backupRPC: environment.bsc.backupRPC,
        }),
      })
    ],    )
    this.metaConnector = new MetaMaskConnector({
      chains: chains,
    })

      // ============= create we3Auth modal ===================

    this.web3AuthInstance = new Web3Auth({
      clientId: web3clientId,
      web3AuthNetwork: environment.production ? "mainnet" : "testnet",
      chainConfig: {
        chainNamespace: CHAIN_NAMESPACES.EIP155,
        chainId: "0x" + chains[0].id.toString(16),
        // rpcTarget: environment.eth.chainInfo.params[0].rpcUrls[0],
      },
      uiConfig: {
        appName:'Best Wallet',
        theme: "light",
        loginMethodsOrder: ["google"],
        appLogo: "./assets/images/logo-blue.png", // Your App Logo Here
      }
    });
    // ============= add open login adapter ===================

    const openloginAdapter = new OpenloginAdapter({
      adapterSettings: {
        clientId: web3clientId,
        network: environment.production ? "mainnet" : "testnet",
        uxMode: "popup",
        whiteLabel: {
          name: "Best Wallet",
          logoLight: "https://d37n5965hfopk3.cloudfront.net/assets/images/logo-white.png",
          logoDark: "https://d37n5965hfopk3.cloudfront.net/assets/images/logo-blue.png",
          defaultLanguage: "en",
          dark: false, // whether to enable dark mode. defaultValue: false
        },
        loginConfig: {
          google: {
            name: "Best Wallet",
            verifier: "best-wallet-google",
            typeOfLogin: "google",
            clientId: googleClientId,
          },
        },
      },
      loginSettings: {
        mfaLevel: "optional", // Pass on the mfa level of your choice: default, optional, mandatory, none
      },
    });

    this.addTorusPlugin(this.web3AuthInstance);
    this.web3AuthInstance.configureAdapter(openloginAdapter);


    // ============= create Web3Auth connector ===================

    this.web3AuthConnector = new Web3AuthConnector({
      chains,
      options: {
        web3AuthInstance: this.web3AuthInstance,
        modalConfig: {
          [WALLET_ADAPTERS.OPENLOGIN]: {
            label: 'Best Wallet',
          loginMethods: {
              google: {
                name: "Google login",
                logoDark: "./assets/images/logo-blue.svg",
                logoLight: "./assets/images/logo-white.svg",
              },
              facebook: {
                // it will hide the facebook option from the Web3Auth modal.
                name: "facebook login",
                showOnModal: false,
              },
              twitter: {
                // it will hide the twitter option from the Web3Auth modal.
                name: "twitter login",
                showOnModal: false,
              },
              reddit: {
                // it will hide the reddit option from the Web3Auth modal.
                name: "reddit login",
                showOnModal: false,
              },
              discord: {
                // it will hide the discord option from the Web3Auth modal.
                name: "discord login",
                showOnModal: false,
              },
              twitch: {
                // it will hide the twitch option from the Web3Auth modal.
                name: "twitch login",
                showOnModal: false,
              },
              apple: {
                // it will hide the apple option from the Web3Auth modal.
                name: "apple login",
                showOnModal: false,
              },
              line: {
                // it will hide the line option from the Web3Auth modal.
                name: "line login",
                showOnModal: false,
              },
              github: {
                // it will hide the github option from the Web3Auth modal.
                name: "github login",
                showOnModal: false,
              },
              kakao: {
                // it will hide the kakao option from the Web3Auth modal.
                name: "kakao login",
                showOnModal: false,
              },
              linkedin: {
                // it will hide the linkedin option from the Web3Auth modal.
                name: "linkedin login",
                showOnModal: false,
              },
              weibo: {
                // it will hide the weibo option from the Web3Auth modal.
                name: "weibo login",
                showOnModal: false,
              },
              wechat: {
                // it will hide the wechat option from the Web3Auth modal.
                name: "wechat login",
                showOnModal: false,
              }
            },
          },
          [WALLET_ADAPTERS.WALLET_CONNECT_V1]: {
            showOnModal: false,
            label: '',
          },
          [WALLET_ADAPTERS.WALLET_CONNECT_V2]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.TORUS_EVM]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.METAMASK]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.COINBASE]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.PHANTOM]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.SLOPE]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.SOLFLARE]: {
            label: '',
            showOnModal: false,
          },
          [WALLET_ADAPTERS.TORUS_SOLANA]: {
            label: '',
            showOnModal: false,
          },
        },
      },
    })

    // this.client = createClient({
    //   autoConnect: true,
    //   connectors: [this.metaConnector, this.walletConnector, this.metaWalletConnector, this.web3AuthConnector],
    //   provider,
    //   webSocketProvider,
    // });

    watchNetwork((network) => {
      this.chainId = network?.chain?.id;
      this.eventService.isBsc = this.chainId == environment.bsc.chainIdInt;
      this.ref.tick();
      console.log('watchNetwork =', network);
    });

    watchAccount((account: any) => this.setData(account));
    if(this.checkWallet()) setTimeout(() => this.connectWallet('web3auth'), 2500);
  }

  // ============= Torus Wallet Connector Plugin ===================

  async addTorusPlugin(webAuthInstance: Web3Auth){
    this.torusPlugin = new TorusWalletConnectorPlugin({
      torusWalletOpts: {
        buttonPosition: 'bottom-right',
        buttonSize: 70,
        apiKey: torusWalletId
      },
      walletInitOptions: {
        whiteLabel: {
          theme: { isDark: false, colors: { torusBrand1: "#286CB2" } },
          logoLight: "https://d37n5965hfopk3.cloudfront.net/assets/images/logo-white.png",
          logoDark: "https://d37n5965hfopk3.cloudfront.net/assets/images/logo-blue.png",
        },
        useWalletConnect: true,
        enableLogging: true,
      },
    });
    await webAuthInstance.addPlugin(this.torusPlugin);
  }

  isCorrectChain(): boolean {
    return this.eventService.isBsc ? (this.chainId === environment.bsc.chainIdInt) : (this.chainId === environment.eth.chainIdInt);
  }

  async connectWallet(mode: string) {
    await connect({
      connector : mode === 'metamask' ? this.metaConnector
                : mode === 'wallet' ? this.walletConnector
                : mode === 'metawallet' ? this.metaWalletConnector
               : this.web3AuthConnector
     })
    .then((success: any)=>{
      console.log('\n======= wallet connected ==========\n', this.client,'\n======================');
      setTimeout(() => {
        this.sendDashFx('', 0, 0, true);
        this.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0 );
      }, 2000);
    },
    (err)=>{
      console.log('\n======= wallet connection issue ==========\n');
      console.log('connection issue =', err);
    })
  }

  async disConnectWallet() {
    await disconnect()
    .then((success: any)=>{
      console.log('\n======= wallet disconnected ==========\n',this.client);
      localStorage.clear();
      this.chainId = null;
      this.walletAddress = '';
    },
    (err)=>{
      console.log('\n======= wallet disconnect issue ==========\n');
      console.log('disconnect issue =', err);
    })
  }

  setData(account: any): void {
    this.wagmiData = account;
    this.walletAddress = this.wagmiData.address;
    this.ref.tick();
    this.getPresalesData();
    console.log('wagmiData', this.wagmiData);
  }

  async getPresalesData() {
    const config = {
      contracts: [
        {
          ...presaleContractBsc,
          functionName: 'currentStep',
          chainId: environment.bsc.chainIdInt, //0
          args: []
        },
        {
          ...presaleContractBsc,
          functionName: 'totalTokensSold',
          chainId: environment.bsc.chainIdInt, //1
          args: []
        },
        {
          ...presaleContractBsc,
          functionName: 'userDeposits',
          chainId: environment.bsc.chainIdInt, //2
          args: [this.walletAddress]
        },
        {
          ...presaleContractBsc,
          functionName: 'bnbBuyHelper',
          chainId: environment.bsc.chainIdInt, //3
          args: [1]
        },
        {
          ...presaleContractBsc,
          functionName: 'usdtBuyHelper',
          chainId: environment.bsc.chainIdInt, //4
          args: [1]
        },
        {
          ...presaleContractBsc,
          functionName: 'usdRaised',
          chainId: environment.bsc.chainIdInt, //5
          args: []
        },
        {
          ...presaleContractBsc,
          functionName: 'checkPoint',
          chainId: environment.bsc.chainIdInt, //6
          args: []
        },
        {
          ...presaleContractEth,
          functionName: 'userDeposits',
          chainId: environment.eth.chainIdInt, //7
          args: [this.walletAddress]
        },
        {
          ...presaleContractEth,
          functionName: 'totalTokensSold',
          chainId: environment.eth.chainIdInt, //8
          args: []
        },
        {
          ...presaleContractEth,
          functionName: 'ethBuyHelper',
          chainId: environment.eth.chainIdInt, //9
          args: [1]
        },
        {
          ...presaleContractEth,
          functionName: 'usdtBuyHelper',
          chainId: environment.eth.chainIdInt, //10
          args: [1]
        },
        {
          ...presaleContractEth,
          functionName: 'usdRaised',
          chainId: environment.eth.chainIdInt, //11
          args: []
        },
        {
          ...presaleContractEth,
          functionName: 'checkPoint',
          chainId: environment.eth.chainIdInt, //12
          args: []
        },
        {
          ...presaleContractEth,              // 13
          functionName: 'roundDetails ',
          chainId: environment.eth.chainIdInt,
          args: [2]
        },
      ]
    }
    this.presaleData = await readContracts(config);
    this.balanceData.currentStep = this.presaleData[0].toNumber();
    let checkpointBSC = this.presaleData[6].toNumber();
    let checkpointETH = this.presaleData[12].toNumber();
    let ethRaised = +ethers.utils.formatEther(this.presaleData[5] || 0);
    let bscRaised = +ethers.utils.formatEther(this.presaleData[11] || 0);

    this.balanceData.bscTokenSold = checkpointBSC > 0 ? checkpointBSC : this.presaleData[1].toNumber();
    this.balanceData.ethTokenSold = checkpointETH > 0 ? checkpointETH : this.presaleData[8].toNumber();
    this.balanceData.totalTokensSold = this.balanceData.bscTokenSold + this.balanceData.ethTokenSold;
    this.balanceData.usdRaised = ethRaised + bscRaised;
    let counter = 0;
    for (let time of this.presaleData[13]){
      this.tokenomics[counter].endDate = time.toNumber();
      counter++;
    }

    if (this.wagmiData?.isConnected) this.getMetaData();
    if(this.refreshId) clearTimeout(this.refreshId);
    this.refreshId = setTimeout(() => this.getPresalesData(), this.refreshRate);

    // console.log('\n******\presaleData =', this.presaleData, '\n******\n');
    // console.log('\n******\nbalanceData =', this.balanceData, '\n******\n');
  }

  async getMetaData() {
    let nativeBalance = await fetchBalance({
      address: this.walletAddress as any,
      formatUnits: 'ether',
    })
    let usdtBal = await fetchBalance({
      address: this.walletAddress as any,
      // formatUnits: 'mwei',
      token: this.eventService.isBsc ? environment.bsc.usdTAddress as any : environment.eth.usdTAddress as any,
      chainId: this.eventService.isBsc ? environment.bsc.chainIdInt : environment.eth.chainIdInt
    })

    this.balanceData.nativeBal = +nativeBalance.formatted;
    this.balanceData.usdtBal = +usdtBal.formatted;
    this.balanceData.bscClaimable = +ethers.utils.formatEther(this.presaleData[2] || 0)
    this.balanceData.ethClaimable = +ethers.utils.formatEther(this.presaleData[7] || 0)
    this.balanceData.userClaimable = this.balanceData.ethClaimable + this.balanceData.bscClaimable;
    // console.log('\n******\nNative Balance =', this.balanceData, '\n******\n');
  }

  get tokenSoldData() {
    for (let soldData of this.tokenomics) {
      if (soldData.maxToken >= this.soldToken && soldData.endDate > (Date.parse(new Date().toString())/1000)) {
        return soldData;
      }
    }
    return this.tokenomics[this.tokenomics.length - 1];
  }

  get soldToken() {
    return this.balanceData.totalTokensSold;
    // return this.balanceData.usdRaised;
  }

  async switchNetwork(chainId: number) {
    await switchNetwork({
      chainId: chainId,
    }).then((success: any) => {
      this.ref.tick();
    },
      (err) => {
        const chain = this.eventService.isBsc ?
          chainId === environment.bsc.chainIdInt ? environment.bsc.chainInfo
          : environment.eth.chainInfo
          : environment.bsc.chainInfo;
        const chainName = chain.params[0].chainName;
        this.popupService.messagePopup("info", this.transletService.instant('switchNetwork', {chain_name: chainName}));
      });
  }

  getEthAmount = async (amount: number) => {
    console.log('getEthAmount called')
    let singleAmount = +ethers.utils.formatEther(this.presaleData[9]);
    return Math.floor(+(amount / (+singleAmount)));
  }

  getBnbAmount = async (amount: number) => {
    console.log('getBnbAmount called')
    let singleAmount = +ethers.utils.formatEther(this.presaleData[3]);
    return Math.floor(+(amount / (+singleAmount)));
  }

  getUSDTAmount = async (amount: number) => {
    let singleAmount = this.eventService.isBsc ? (this.presaleData[4] / Math.pow(10, 18)) : ((this.presaleData[10] / Math.pow(10, 6)));
    return Math.floor(+(amount / (+singleAmount)));
  }

  getDynamicAmount = async (amount: number, mode: string) => {
    const contractData = await readContracts({
      contracts: [
        {
          ...presaleContractBsc,              // 0
          functionName: 'bnbBuyHelper',
          chainId: environment.bsc.chainIdInt,
          args: [Math.floor(amount)]
        },
        {
          ...presaleContractBsc,              // 1
          functionName: 'usdtBuyHelper',
          chainId: environment.bsc.chainIdInt,
          args: [Math.floor(amount)]
        },
        {
          ...presaleContractEth,              // 2
          functionName: 'ethBuyHelper',
          chainId: environment.eth.chainIdInt,
          args: [Math.floor(amount)]
        },
        {
          ...presaleContractEth,              // 3
          functionName: 'usdtBuyHelper',
          chainId: environment.eth.chainIdInt,
          args: [Math.floor(amount)]
        }
      ]
    });

    let singleAmount = 0;
    if( mode === 'getBnbAmount' && this.eventService.isBsc){
      singleAmount = +ethers.utils.formatEther(contractData[0] as any);
      return +singleAmount;
    }
    else if( mode === 'getUSDTAmount' && this.eventService.isBsc){
      console.log(mode, amount, contractData[1] as any)
      singleAmount = +contractData[1] as any / Math.pow(10, 18);
      return +singleAmount;
    }
    else if (mode === 'getEthAmount' && !this.eventService.isBsc) {
      singleAmount = +ethers.utils.formatEther(contractData[2] as any);
      console.log('wwwwwwwwww')
      return +singleAmount;
    }
    else if (mode === 'getUSDTAmount' && !this.eventService.isBsc) {
      singleAmount = +contractData[3] as any / Math.pow(10, 6);
      return +singleAmount;
    }
    else return singleAmount;
  }

  //  ************* Swap ETH Token **************

  async swapEthTokens(amount: number, value: number) {
    console.log('swapEthTokens called');
    this.swapStatus = SwapStatus.not_started;
    const ethConfig = await prepareWriteContract({
      ...presaleContractEth,
      functionName: 'buyWithEth',
      chainId: this.chainId,
      args:[amount],
      overrides: {
        from: this.walletAddress,
        value: ethers.utils.parseEther(value.toString()),
      },
    })
    this.swapStatus = SwapStatus.confirm_pending;
    await writeContract(ethConfig)
    .then((success: any) =>{
      console.log('Eth write Contract success', success);
      this.swapStatus = SwapStatus.in_progess;
      this.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0);
      this.sendDashFx('ETH', amount, value);
      this.checkTRansaction(success.hash, 'ETH', amount, value);
    },
    (err: any)=>{
      if(err.code === 4001){
        this.swapStatus = SwapStatus.rejected;
      }else{
        this.swapStatus = SwapStatus.failed;
      }
    });

  }

//  ************* Swap BNB Token **************

  async swapBnbTokens(amount: number, value: number) {
    console.log('swapBnbTokens called');
    this.swapStatus = SwapStatus.not_started;
    const config = await prepareWriteContract({
      ...presaleContractBsc,
      functionName: 'buyWithBNB',
      chainId: environment.bsc.chainIdInt,
      args:[amount],
      overrides: {
        from: this.walletAddress,
        value: ethers.utils.parseEther(value.toString()),
      },
    });

    this.swapStatus = SwapStatus.confirm_pending;
    await writeContract(config)
    .then((success: any) =>{
      console.log('Bnb write Contract success', success);
      this.swapStatus = SwapStatus.in_progess;
      this.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0);
      this.sendDashFx('BNB', amount, value);
      this.checkTRansaction(success.hash, 'BNB', amount, value);
    },
    (err: any)=>{
      if(err.code === 4001){
        this.swapStatus = SwapStatus.rejected;
      }else{
        this.swapStatus = SwapStatus.failed;
      }
    });
  }

  //  ************* Swap USDT Token **************

  async swapCryptoForUSDT(amount: number, value: number) {

    const tokenContract = {
      address: this.eventService.isBsc ? environment.bsc.usdTAddress as any : environment.eth.usdTAddress as any,
      abi: token.abi,
    }
    const presaleAdd = this.eventService.isBsc ? environment.bsc.presaleAddress as any : environment.eth.presaleAddress as any;

    const allowanceData = await readContracts({
      contracts: [
        {
          ...tokenContract,
          functionName: 'allowance',
          chainId: this.chainId,
          args:[this.walletAddress, presaleAdd],
        },
      ],
    })
    const allowanceValue = +ethers.utils.formatEther(allowanceData[0] as any);
    console.log('allowanceData =', value, allowanceValue);

    if (+value > +allowanceValue) {
      const approveConfig = await prepareWriteContract({
        ...tokenContract,
        chainId: this.chainId,
        functionName: 'approve',
        args:[presaleAdd, '100000000000000000000000000'],
        overrides: {
          from: this.walletAddress
        },
      })

      console.log('approveConfig =', approveConfig)
      this.swapStatus = SwapStatus.approval_pending;
        const { hash } =  await writeContract(approveConfig);
        console.log('hash =', hash)
        await waitForTransaction({ hash })
        .then((success: any) =>{
          console.log('approve Config success', success);
          this.buyUSDT(amount, value);
        },
        (err: any)=>{
          console.log('approve Config error', err);
          this.swapStatus = SwapStatus.rejected;
        });
    }else{
      this.buyUSDT(amount, value);
    }
  }


  //  ************* Buy USDT **************

  async buyUSDT(amount: number,value: number){
    const contract = this.eventService.isBsc ? presaleContractBsc : presaleContractEth;
    const usdtConfig = await prepareWriteContract({
      ...contract,
      functionName: 'buyWithUSDT',
      chainId: this.chainId,
      args:[amount],
      overrides: {
        from: this.walletAddress
      },
    });

    console.log('usdtConfig =', usdtConfig)
    this.swapStatus = SwapStatus.confirm_pending;
    await writeContract(usdtConfig)
    .then((success: any) =>{
      console.log('Eth writeContract success', success);
      this.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0);
      this.swapStatus = SwapStatus.in_progess;
      this.sendDashFx('usdt', amount, value);
      this.checkTRansaction(success.hash, 'usdt', amount, value);
    },
    (err: any)=>{
      console.log('Eth writeContract error', err);
        this.swapStatus = SwapStatus.failed;
    });
  }

  async checkTRansaction(haskKey: string, mode: string, amount: number,value: number) {
    await waitForTransaction({ hash: haskKey as any})
    .then((response: any) =>{
      console.log('checkTansaction success', response);
      this.swapStatus = SwapStatus.complete;
      this.swapHash = response.transactionHash;
      this.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, mode, environment.tokenName, value, amount );
    },
    (err: any)=>{
      console.log('checkTansaction error', err);
      this.swapStatus = SwapStatus.failed;
    });
  }

  windowDataLayer(flowName: string, stepName: string, stepNo: number, completeFlag: number, errorCode: number, fromCurrency: string = '', toCurrency: string ='', fromVal: number = 0, toVal: number = 0 ): void {

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "workflowStep",
      walletAddress:        this.walletAddress,
      workflowName:         flowName,           // "swap",
      workflowStepNumber:   stepNo,             // 2,
      workflowStepName:     stepName,           // "confirmTransaction",

      workflowCompleteFlag: completeFlag,
      workflowErrorCode:    errorCode,

      transactionId:        completeFlag === 0 ? undefined : this.swapHash,
      swapFromValue:        completeFlag === 0 ? undefined : fromVal,              // value,
      swapToValue:          completeFlag === 0 ? undefined : toVal,                // amount,
      swapFromCurrency:     completeFlag === 0 ? undefined : fromCurrency,         // "USDT",
      swapToCurrency:       completeFlag === 0 ? undefined : toCurrency,           // "$BWAT"
    });
  }

   // ==== DashFx ====

   sendDashFx = (type: string, toValue: number, fromValue: number, connectMode: boolean = false) => {
    const tokens: any = (this.balanceData.ethClaimable || 0) + (this.balanceData.bscClaimable || 0);
    const data = {
      walletAddress: this.walletAddress,
      iid: environment.iidDashFx,
      event: connectMode ? 'lead_success' : (tokens > toValue ? 'revenue' : 'conversion'),
      purchaseType: connectMode ? null : type,
      purchaseTokens: +toValue,
      purchaseTypeAmount: +fromValue,
      purchaseUsdAmount: type === 'usdt' ? +fromValue : +(+(toValue * this.tokenSoldData.tokenUSDT).toFixed(2)),
      ipAddress: '',
      clickId: getClickId()
    }
    console.log('DashFx data =', tokens, data)
    this.http.post(environment.urlDashFx, data).subscribe((res: any) => {
      console.log('DashFx success response', res);
    },
    (err: any) => {
      console.log('DashFx error response', err);
    })
  }

  getDynamicAmountWert = async (amount: number) => {
    const contractData = await readContracts({
      contracts: [
        {
          ...presaleContractEth,
          functionName: 'ethBuyHelper',
          chainId: environment.eth.chainIdInt,
          args: [amount]
        }
      ]
    });
    let ehtVal = +ethers.utils.formatEther(contractData[0] as any);
    return +ehtVal;
  }

  async getABI(amount: number): Promise<string> {
    let ABI = presaleEth.abi;
    let iface = new ethers.utils.Interface(ABI);
    const sc_input_data = iface.encodeFunctionData("buyWithETHWert", [this.walletAddress, amount]);
    console.log('sc_input_data =', sc_input_data);
    return sc_input_data
  }

  checkWallet(): boolean {
    const isConnected = localStorage.getItem('wagmi.connected');
    const isWallet = localStorage.getItem('wagmi.wallet');
    if(isConnected === 'true' && isWallet === '"web3auth"') return true;
    return false
  }
}
